/**
 * AgentNetworkPanel — pestaña «Red» del agente CMDB.
 *
 * Tres bloques, en el orden en el que se configuran de verdad:
 *   1. Colectores registrados + «Escanear ahora».
 *   2. Credenciales de descubrimiento (perfiles reutilizables, nunca por equipo).
 *   3. Ámbitos de barrido (rangos o controladores) + últimas ejecuciones.
 *
 * Convenciones de la sección Agente: texto en español a pelo (no i18n), sin
 * emojis, `Panel` para tarjetas y las clases `list-*` del estándar de listados.
 * Los secretos NUNCA se muestran: al editar solo se envían si se reescriben.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  BookOpenIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  KeyIcon,
  PencilSquareIcon,
  PlayIcon,
  PlusIcon,
  SignalIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import Dialog from './common/Dialog';
import { Panel } from './common/Panel';
import AgentNetworkGuide from './AgentNetworkGuide';
import { hace } from '../utils/relativeTime';

const CREDENTIAL_KINDS = [
  { value: 'snmp_v2c', label: 'SNMP v2c' },
  { value: 'snmp_v3', label: 'SNMP v3' },
  { value: 'snmp_v1', label: 'SNMP v1' },
  { value: 'ssh', label: 'SSH' },
  { value: 'winrm', label: 'WinRM (Windows)' },
  { value: 'unifi', label: 'UniFi (API)' },
];

const KIND_LABEL = Object.fromEntries(CREDENTIAL_KINDS.map((k) => [k.value, k.label]));

const RUN_BADGE = {
  ok: 'list-badge--on',
  partial: 'list-badge--warn',
  error: 'list-badge--off',
  running: 'list-badge--muted',
};

function Paso({ numero, titulo, detalle, done, action }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
        padding: '9px 2px', borderBottom: '1px solid var(--border)',
      }}
    >
      <span
        className={`list-badge ${done ? 'list-badge--on' : 'list-badge--muted'}`}
        style={{ minWidth: 26, justifyContent: 'center' }}
      >
        {done ? <CheckIcon style={{ width: 13, height: 13 }} /> : numero}
      </span>
      <div style={{ flex: '1 1 320px' }}>
        <div style={{ fontWeight: 600, fontSize: '0.86rem', color: 'var(--text-primary)' }}>{titulo}</div>
        <div className="list-hint">{detalle}</div>
      </div>
      {!done && action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
    </div>
  );
}

function Fila({ children, actions }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        padding: '8px 2px', borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
      {actions && <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>{actions}</div>}
    </div>
  );
}

/* ── Diagnóstico del barrido: qué ha pasado con cada IP ───────────────────── */

const PROBE_REASON = {
  ok: 'Contestó',
  timeout: 'Sin respuesta (timeout)',
  auth: 'Credencial rechazada',
  collect: 'Contestó, pero falló la lectura',
  snmp: 'Error de SNMP',
  ssh: 'SSH sin datos',
  error: 'Error inesperado',
  sin_credenciales: 'Sin credencial SNMP',
  'sin respuesta': 'Sin respuesta',
};

function ipOrden(ip) {
  const partes = String(ip || '').split('.').map((parte) => Number(parte));
  if (partes.length !== 4 || partes.some((n) => Number.isNaN(n))) return 0;
  return ((partes[0] * 256 + partes[1]) * 256 + partes[2]) * 256 + partes[3];
}

function tieneInformeIp(run) {
  const resumen = run?.summary?.probe_summary;
  return Boolean(resumen && Object.keys(resumen).length > 0);
}

function ProbeDialog({ run, onClose }) {
  const summary = run?.summary || {};
  const probeSummary = summary.probe_summary || {};
  const ok = summary.probes_ok || [];
  const failed = summary.probes_failed || [];
  const [filtro, setFiltro] = useState(failed.length ? 'fallos' : 'ok');
  const [copiado, setCopiado] = useState(false);

  const filas = (filtro === 'ok' ? [] : failed)
    .concat(filtro === 'fallos' ? [] : ok.map((ip) => ({ ip, reason: 'ok', detail: '' })))
    .slice()
    .sort((a, b) => ipOrden(a.ip) - ipOrden(b.ip));

  const descartes = Object.entries(probeSummary)
    .filter(([motivo, valor]) => motivo !== 'ok' && valor)
    .sort((a, b) => b[1] - a[1]);

  const copiar = async () => {
    const texto = filas
      .map((fila) => `${fila.ip}\t${fila.reason}\t${fila.detail || ''}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch (e) {
      setCopiado(false);
    }
  };

  return (
    <Dialog
      title={`Diagnóstico del barrido · ${run?.started_at ? new Date(run.started_at).toLocaleString() : ''}`}
      onClose={onClose}
      maxWidth={760}
      footer={(
        <>
          <button type="button" className="list-btn-secondary" onClick={copiar}>
            {copiado ? 'Copiado' : 'Copiar lista'}
          </button>
          <button type="button" className="list-btn-primary" onClick={onClose}>
            Cerrar
          </button>
        </>
      )}
    >
      <p className="list-hint" style={{ marginTop: 0 }}>
        {probeSummary.ok || 0} de {summary.targets_total || '?'} IP(s) contestaron
        {descartes.length > 0 && (
          <> · descartadas: {descartes.map(([motivo, valor]) => `${valor} ${motivo}`).join(', ')}</>
        )}
        {summary.probes_truncated ? ' · la lista se ha recortado (rango muy grande)' : ''}
      </p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          className={filtro === 'fallos' ? 'list-btn-primary' : 'list-btn-secondary'}
          onClick={() => setFiltro('fallos')}
        >
          Descartadas ({failed.length})
        </button>
        <button
          type="button"
          className={filtro === 'ok' ? 'list-btn-primary' : 'list-btn-secondary'}
          onClick={() => setFiltro('ok')}
        >
          Respondieron ({ok.length})
        </button>
        <button
          type="button"
          className={filtro === 'todas' ? 'list-btn-primary' : 'list-btn-secondary'}
          onClick={() => setFiltro('todas')}
        >
          Todas
        </button>
      </div>

      {filas.length === 0 ? (
        <p className="list-hint" style={{ margin: 0 }}>
          {failed.length || ok.length
            ? 'Nada que enseñar con este filtro.'
            : 'El colector no mandó diagnóstico por IP en este barrido: actualiza el agente a 2.2.8 o superior y vuelve a lanzarlo.'}
        </p>
      ) : (
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
          {filas.map((fila) => (
            <div
              key={fila.ip}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '6px 2px',
                borderBottom: '1px solid var(--border)', flexWrap: 'wrap',
              }}
            >
              <span className="list-code" style={{ minWidth: 110 }}>{fila.ip}</span>
              <span
                className={`list-badge ${fila.reason === 'ok' ? 'list-badge--on' : 'list-badge--muted'}`}
              >
                {PROBE_REASON[fila.reason] || fila.reason}
              </span>
              {fila.ms ? <span className="list-hint">{fila.ms} ms</span> : null}
              {fila.detail ? (
                <span className="list-hint" style={{ flex: 1, minWidth: 200 }}>{fila.detail}</span>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
}

/* ── Diálogo de credencial ─────────────────────────────────────────────────── */

function CredentialDialog({ initial, onClose, onSave }) {
  const isEdit = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name || '');
  const [kind, setKind] = useState(initial?.kind || 'snmp_v2c');
  const [vendor, setVendor] = useState(initial?.vendor || '');
  const [priority, setPriority] = useState(initial?.priority ?? 100);
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [options, setOptions] = useState({
    port: 161,
    timeout: 3,
    retries: 1,
    ...(initial?.options || {}),
  });
  const [secret, setSecret] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setOption = (key, value) => setOptions((prev) => ({ ...prev, [key]: value }));
  const setSecretField = (key, value) => setSecret((prev) => ({ ...prev, [key]: value }));

  const defaultPortFor = (value) => (
    value === 'ssh' ? 22
      : value === 'winrm' ? 5986
        : (value === 'unifi' || value === 'api') ? 443
          : 161
  );

  // Al cambiar de tipo, el puerto por defecto del tipo anterior no sirve:
  // SNMP es 161/udp, SSH 22/tcp y UniFi 443/tcp. Si el usuario no lo ha tocado,
  // se cambia solo (si no, el colector intenta https://host:161).
  const changeKind = (next) => {
    const previousDefault = defaultPortFor(kind);
    setKind(next);
    setOptions((prev) => ({
      ...prev,
      port: !prev.port || Number(prev.port) === previousDefault
        ? defaultPortFor(next)
        : prev.port,
    }));
  };

  const guardar = async () => {
    setError('');
    if (!name.trim()) { setError('El nombre es obligatorio'); return; }
    const snmpVersion = kind === 'snmp_v1' ? '1' : kind === 'snmp_v3' ? '3' : '2c';
    const payload = {
      name: name.trim(),
      kind,
      vendor: vendor.trim(),
      priority: Number(priority) || 100,
      enabled,
      options: {
        ...options,
        // Nunca heredar el 161 de SNMP en una credencial HTTPS.
        port: kind === 'winrm'
          ? ([22, 161, 443].includes(Number(options.port))
            ? 5986
            : Number(options.port) || 5986)
          : (kind === 'unifi' || kind === 'api') && [22, 161].includes(Number(options.port))
            ? 443
            : Number(options.port) || defaultPortFor(kind),
        timeout: Number(options.timeout) || 3,
        retries: Number(options.retries) || 0,
        ...(kind.startsWith('snmp') ? { snmp_version: snmpVersion } : {}),
      },
    };
    if (!isEdit || Object.keys(secret).length > 0) payload.secret = secret;
    if (!isEdit && Object.keys(secret).length === 0) {
      setError('Rellena los datos del secreto de la credencial');
      return;
    }
    setSaving(true);
    try {
      await onSave(payload);
      onClose();
    } catch (e) {
      setError(e.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      title={isEdit ? 'Editar credencial de red' : 'Nueva credencial de red'}
      onClose={onClose}
      busy={saving}
      maxWidth={560}
      footer={(
        <>
          <button type="button" className="list-btn-secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="list-btn-primary" onClick={guardar} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      )}
    >
      {error && <div className="list-badge list-badge--off" style={{ display: 'block', marginBottom: 10 }}>{error}</div>}

      <label className="list-hint" style={{ display: 'block', marginBottom: 10 }}>
        Nombre
        <input className="list-input" style={{ width: '100%', marginTop: 4 }} value={name}
          onChange={(e) => setName(e.target.value)} placeholder="Comunidad VLAN gestión" />
      </label>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <label className="list-hint" style={{ flex: '1 1 180px' }}>
          Tipo
          <select className="list-select" style={{ width: '100%', marginTop: 4 }} value={kind}
            onChange={(e) => changeKind(e.target.value)}>
            {CREDENTIAL_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </label>
        <label className="list-hint" style={{ flex: '1 1 160px' }}>
          Prioridad
          <input className="list-input" type="number" style={{ width: '100%', marginTop: 4 }} value={priority}
            onChange={(e) => setPriority(e.target.value)} />
        </label>
        <label className="list-hint" style={{ flex: '1 1 140px' }} title="Solo informativo: para SNMP/SSH sirve de ayuda, para UniFi/API no se usa">
          Fabricante (opcional)
          <input className="list-input" style={{ width: '100%', marginTop: 4 }} value={vendor}
            onChange={(e) => setVendor(e.target.value)} placeholder="unifi / cisco" />
        </label>
      </div>

      {(kind === 'snmp_v1' || kind === 'snmp_v2c') && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <label className="list-hint" style={{ flex: '2 1 220px' }}>
            Community
            <input className="list-input" type="password" style={{ width: '100%', marginTop: 4 }}
              value={secret.community || ''} onChange={(e) => setSecretField('community', e.target.value)}
              placeholder={isEdit && initial?.has_secret ? '•••••• (sin cambios)' : 'public'} />
          </label>
          <label className="list-hint" style={{ flex: '0 1 100px' }}>
            Puerto
            <input className="list-input" type="number" style={{ width: '100%', marginTop: 4 }}
              value={options.port || 161} onChange={(e) => setOption('port', e.target.value)} />
          </label>
          <label className="list-hint" style={{ flex: '0 1 100px' }}>
            Timeout (s)
            <input className="list-input" type="number" style={{ width: '100%', marginTop: 4 }}
              value={options.timeout || 3} onChange={(e) => setOption('timeout', e.target.value)} />
          </label>
        </div>
      )}

      {kind === 'snmp_v3' && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <label className="list-hint" style={{ flex: '1 1 200px' }}>
              Usuario
              <input className="list-input" style={{ width: '100%', marginTop: 4 }}
                value={secret.user || ''} onChange={(e) => setSecretField('user', e.target.value)} />
            </label>
            <label className="list-hint" style={{ flex: '1 1 150px' }}>
              Auth
              <select className="list-select" style={{ width: '100%', marginTop: 4 }}
                value={secret.auth_protocol || 'sha'} onChange={(e) => setSecretField('auth_protocol', e.target.value)}>
                <option value="md5">MD5</option>
                <option value="sha">SHA-1</option>
                <option value="sha256">SHA-256</option>
                <option value="sha512">SHA-512</option>
              </select>
            </label>
            <label className="list-hint" style={{ flex: '1 1 180px' }}>
              Clave de autenticación
              <input className="list-input" type="password" style={{ width: '100%', marginTop: 4 }}
                value={secret.auth_key || ''} onChange={(e) => setSecretField('auth_key', e.target.value)} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <label className="list-hint" style={{ flex: '1 1 150px' }}>
              Priv
              <select className="list-select" style={{ width: '100%', marginTop: 4 }}
                value={secret.priv_protocol || 'aes'} onChange={(e) => setSecretField('priv_protocol', e.target.value)}>
                <option value="des">DES</option>
                <option value="aes">AES-128</option>
              </select>
            </label>
            <label className="list-hint" style={{ flex: '1 1 180px' }}>
              Clave de privacidad
              <input className="list-input" type="password" style={{ width: '100%', marginTop: 4 }}
                value={secret.priv_key || ''} onChange={(e) => setSecretField('priv_key', e.target.value)} />
            </label>
            <label className="list-hint" style={{ flex: '1 1 120px' }}>
              Contexto
              <input className="list-input" style={{ width: '100%', marginTop: 4 }}
                value={secret.context_name || ''} onChange={(e) => setSecretField('context_name', e.target.value)} />
            </label>
          </div>
        </>
      )}

      {kind === 'ssh' && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <label className="list-hint" style={{ flex: '1 1 180px' }}>
            Usuario
            <input className="list-input" style={{ width: '100%', marginTop: 4 }}
              value={secret.username || ''} onChange={(e) => setSecretField('username', e.target.value)} />
          </label>
          <label className="list-hint" style={{ flex: '1 1 180px' }}>
            Contraseña
            <input className="list-input" type="password" style={{ width: '100%', marginTop: 4 }}
              value={secret.password || ''} onChange={(e) => setSecretField('password', e.target.value)} />
          </label>
          <label className="list-hint" style={{ flex: '1 1 150px' }}>
            Modelo de dispositivo
            <select className="list-select" style={{ width: '100%', marginTop: 4 }}
              value={options.device_type || 'autodetect'} onChange={(e) => setOption('device_type', e.target.value)}>
              <option value="autodetect">Autodetectar</option>
              <option value="cisco">Cisco IOS</option>
              <option value="aruba">Aruba</option>
              <option value="fortinet">Fortinet</option>
              <option value="mikrotik">MikroTik</option>
              <option value="ubiquiti">Ubiquiti</option>
            </select>
          </label>
        </div>
      )}

      {kind === 'winrm' && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <label className="list-hint" style={{ flex: '1 1 180px' }}>
              Usuario
              <input className="list-input" style={{ width: '100%', marginTop: 4 }}
                value={secret.username || ''} onChange={(e) => setSecretField('username', e.target.value)}
                placeholder="CMDB\svc-inventario" />
            </label>
            <label className="list-hint" style={{ flex: '1 1 180px' }}>
              Contraseña
              <input className="list-input" type="password" style={{ width: '100%', marginTop: 4 }}
                value={secret.password || ''} onChange={(e) => setSecretField('password', e.target.value)}
                placeholder={isEdit && initial?.has_secret ? '•••••• (sin cambios)' : ''} />
            </label>
            <label className="list-hint" style={{ flex: '0 1 130px' }}>
              Dominio (opcional)
              <input className="list-input" style={{ width: '100%', marginTop: 4 }}
                value={secret.domain || ''} onChange={(e) => setSecretField('domain', e.target.value)} />
            </label>
            <label className="list-hint" style={{ flex: '0 1 110px' }}>
              Puerto
              <input className="list-input" type="number" style={{ width: '100%', marginTop: 4 }}
                value={options.port || 5986} onChange={(e) => setOption('port', e.target.value)} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <label className="list-hint" style={{ flex: '0 1 160px' }}>
              Transporte
              <select className="list-select" style={{ width: '100%', marginTop: 4 }}
                value={options.transport || 'ntlm'} onChange={(e) => setOption('transport', e.target.value)}>
                <option value="ntlm">NTLM</option>
                <option value="basic">Basic</option>
                <option value="kerberos">Kerberos</option>
              </select>
            </label>
            <label className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 200px' }}>
              <input type="checkbox" checked={Boolean(options.verify_tls)}
                onChange={(e) => setOption('verify_tls', e.target.checked)} />
              Verificar el certificado TLS
            </label>
          </div>
          <div className="list-hint" style={{ marginTop: -4, marginBottom: 10 }}>
            Inventario <strong>sin agente</strong> de equipos Windows: hardware, SO,
            antivirus y software. El equipo no necesita nada instalado, solo
            <strong> WinRM por HTTPS (5986)</strong> activado por GPO y una cuenta con
            permisos remotos. Con 5985 (HTTP) deja «Verificar TLS» desmarcado.
            Lo que no da esto —BitLocker, parches y eventos— lo aporta el agente.
          </div>
        </>
      )}

      {(kind === 'unifi' || kind === 'api') && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <label className="list-hint" style={{ flex: '1 1 220px' }}>
            Host del controlador
            <input className="list-input" style={{ width: '100%', marginTop: 4 }}
              value={secret.host || ''} onChange={(e) => setSecretField('host', e.target.value)}
              placeholder="192.168.1.1 o unifi.cliente.com" />
          </label>
          <label className="list-hint" style={{ flex: '1 1 240px' }}>
            API key
            <input className="list-input" type="password" style={{ width: '100%', marginTop: 4 }}
              value={secret.api_key || ''} onChange={(e) => setSecretField('api_key', e.target.value)}
              placeholder={isEdit && initial?.has_secret ? '•••••• (sin cambios)' : ''} />
          </label>
          <label className="list-hint" style={{ flex: '0 1 150px' }}>
            Site / host (opcional)
            <input className="list-input" style={{ width: '100%', marginTop: 4 }}
              value={secret.site || ''} onChange={(e) => setSecretField('site', e.target.value)} />
          </label>
          <label className="list-hint" style={{ flex: '0 1 90px' }}>
            Puerto
            <input className="list-input" type="number" style={{ width: '100%', marginTop: 4 }}
              value={options.port || 443} onChange={(e) => setOption('port', e.target.value)} />
          </label>
        </div>
      )}

      {(kind === 'unifi' || kind === 'api') && (
        <div className="list-hint" style={{ marginTop: -4, marginBottom: 10 }}>
          Host = el <strong>UniFi OS del cliente</strong> (IP o dominio, puerto 443) con una
          clave creada en el controlador. Si usas <code>api.ui.com</code>, la clave debe ser
          de <strong>Site Manager</strong> (unifi.ui.com/settings/api-keys) y solo dará
          inventario básico: equipos y sites, sin puertos ni cableado. Vacío en «Site» = todos
          los sites del controlador. Si el Cloud Key o la cuenta ven varios clientes, indica
          el site o el host de este cliente. Puedes comprobarlo con «Probar».
        </div>
      )}

      <label className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Credencial activa
      </label>
    </Dialog>
  );
}

/* ── Diálogo de ámbito ─────────────────────────────────────────────────────── */

function ScopeDialog({ initial, credentials, onClose, onSave }) {
  const isEdit = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name || '');
  const [kind, setKind] = useState(initial?.kind || 'cidr');
  const [cidr, setCidr] = useState(initial?.cidr || '');
  const [credentialIds, setCredentialIds] = useState(
    Array.isArray(initial?.credential_ids) ? initial.credential_ids : [],
  );
  const [exclude, setExclude] = useState((initial?.exclude || []).join(', '));
  const [maxHosts, setMaxHosts] = useState(initial?.max_hosts || 1024);
  const [intervalMinutes, setIntervalMinutes] = useState(initial?.interval_minutes || 1440);
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleCredential = (id) => {
    setCredentialIds((prev) => (
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    ));
  };

  const guardar = async () => {
    setError('');
    if (!name.trim()) { setError('El nombre es obligatorio'); return; }
    if (kind === 'cidr' && !cidr.trim()) { setError('El rango CIDR es obligatorio'); return; }
    if (credentialIds.length === 0) { setError('Selecciona al menos una credencial'); return; }
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        kind,
        cidr: kind === 'cidr' ? cidr.trim() : null,
        credential_ids: credentialIds,
        exclude: exclude.split(',').map((value) => value.trim()).filter(Boolean),
        max_hosts: Number(maxHosts) || 1024,
        interval_minutes: Number(intervalMinutes) || 1440,
        enabled,
      });
      onClose();
    } catch (e) {
      setError(e.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      title={isEdit ? 'Editar ámbito de barrido' : 'Nuevo ámbito de barrido'}
      onClose={onClose}
      busy={saving}
      maxWidth={560}
      footer={(
        <>
          <button type="button" className="list-btn-secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="list-btn-primary" onClick={guardar} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      )}
    >
      {error && <div className="list-badge list-badge--off" style={{ display: 'block', marginBottom: 10 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <label className="list-hint" style={{ flex: '2 1 220px' }}>
          Nombre
          <input className="list-input" style={{ width: '100%', marginTop: 4 }} value={name}
            onChange={(e) => setName(e.target.value)} placeholder="VLAN gestión Madrid" />
        </label>
        <label className="list-hint" style={{ flex: '1 1 150px' }}>
          Tipo
          <select className="list-select" style={{ width: '100%', marginTop: 4 }} value={kind}
            onChange={(e) => setKind(e.target.value)}>
            <option value="cidr">Rango (CIDR)</option>
            <option value="controller">Controlador (API)</option>
          </select>
        </label>
      </div>

      {kind === 'cidr' && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <label className="list-hint" style={{ flex: '1 1 180px' }}>
            Rango CIDR
            <input className="list-input" style={{ width: '100%', marginTop: 4 }} value={cidr}
              onChange={(e) => setCidr(e.target.value)} placeholder="10.10.0.0/24" />
          </label>
          <label className="list-hint" style={{ flex: '1 1 180px' }}>
            Excluir (separado por comas)
            <input className="list-input" style={{ width: '100%', marginTop: 4 }} value={exclude}
              onChange={(e) => setExclude(e.target.value)} placeholder="10.10.0.1, 10.10.0.0/28" />
          </label>
          <label className="list-hint" style={{ flex: '0 1 120px' }}>
            Máx. equipos
            <input className="list-input" type="number" style={{ width: '100%', marginTop: 4 }}
              value={maxHosts} onChange={(e) => setMaxHosts(e.target.value)} />
          </label>
        </div>
      )}

      <div className="list-hint" style={{ marginBottom: 6 }}>Credenciales, en orden de prioridad</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {credentials.length === 0 && (
          <div className="list-hint">Crea primero una credencial de descubrimiento.</div>
        )}
        {credentials.map((credential) => (
          <label key={credential.id} className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={credentialIds.includes(credential.id)}
              onChange={() => toggleCredential(credential.id)}
            />
            <span style={{ fontWeight: 600 }}>{credential.name}</span>
            <span className="list-badge list-badge--muted">{KIND_LABEL[credential.kind] || credential.kind}</span>
            <span className="list-hint">prioridad {credential.priority}</span>
          </label>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <label className="list-hint" style={{ flex: '1 1 180px' }}>
          Cada cuántos minutos
          <input className="list-input" type="number" style={{ width: '100%', marginTop: 4 }}
            value={intervalMinutes} onChange={(e) => setIntervalMinutes(e.target.value)} />
        </label>
        <label className="list-hint" style={{ flex: '1 1 160px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Ámbito activo
        </label>
      </div>
    </Dialog>
  );
}

/* ── Panel principal ───────────────────────────────────────────────────────── */

export default function AgentNetworkPanel({ companyId }) {
  const [collectors, setCollectors] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [notas, setNotas] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [testDialog, setTestDialog] = useState(null);
  const [probeDialog, setProbeDialog] = useState(null);
  const [credentialDialog, setCredentialDialog] = useState(null);
  const [scopeDialog, setScopeDialog] = useState(null);
  const [confirmar, setConfirmar] = useState(null);

  const avisar = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(''), 3500);
  };

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [collectorsRes, credentialsRes, scopesRes, runsRes] = await Promise.all([
        api.getNetworkCollectors(companyId).catch(() => ({ data: [] })),
        api.getNetworkCredentials(companyId).catch(() => ({ data: [] })),
        api.getNetworkScopes(companyId).catch(() => ({ data: [] })),
        api.getNetworkRuns(companyId, 20).catch(() => ({ data: [] })),
      ]);
      setCollectors(collectorsRes?.data || []);
      setCredentials(credentialsRes?.data || []);
      setScopes(scopesRes?.data || []);
      setRuns(runsRes?.data || []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar la configuración de red');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  const lanzarBarrido = async () => {
    setError('');
    if (!scopes.some((scope) => scope.enabled)) {
      setError('No hay ámbitos activos. Crea uno (tipo Controlador para UniFi) antes de escanear.');
      return;
    }
    if (!credentials.some((credential) => credential.enabled)) {
      setError('No hay credenciales activas. Crea una antes de escanear.');
      return;
    }
    if (necesitaColector && collectors.length === 0) {
      setError(
        'Los ámbitos de rango (SNMP/SSH) necesitan un colector instalado en la red. ' +
        'Los de tipo controlador/API se barren desde el servidor.',
      );
      return;
    }
    setScanning(true);
    try {
      const res = await api.triggerNetworkScan(companyId, { reason: 'manual' });
      if (res?.mode === 'server') {
        const errores = res.errors || [];
        const avisos = res.warnings || [];
        const cobertura = res.coverage || {};
        const faltan = cobertura.missing || [];
        const fusionados = cobertura.merged || [];
        // Lo que el barrido NO ha cambiado (la ficha manda) y los duplicados
        // que ha fusionado. Se enseñan aparte para que no pase desapercibido.
        const duplicadosFusionados = cobertura.duplicates_merged || [];
        const diferencias = cobertura.differences || [];
        // Fichas renombradas porque manda el controlador/API: es un cambio
        // hecho, no una diferencia pendiente.
        const renombrados = cobertura.renamed || [];
        const repetidos = cobertura.duplicates || [];
        setNotas([
          ...repetidos.map(
            (d) => `Hay ${d.rows.length} fichas con la MAC ${d.mac}: ` +
              d.rows.map((r) => `«${r.name}»`).join(' / '),
          ),
          ...duplicadosFusionados,
          ...renombrados,
          ...diferencias,
        ].map((n) => (typeof n === 'string' ? n : n.message)));
        avisar(
          `Barrido desde el servidor: ${res.devices || 0} equipo(s) y ${res.wlans || 0} SSID(s)` +
          (res.cables ? ` · ${res.cables} cable(s)` : '') +
          (duplicadosFusionados.length ? ` · ${duplicadosFusionados.length} duplicado(s) fusionado(s)` : '') +
          (renombrados.length ? ` · ${renombrados.length} renombrado(s)` : '') +
          (errores.length ? ` · ${errores.length} aviso(s)` : ''),
        );
        if (faltan.length) {
          setError(
            `El controlador devolvió ${faltan.length} equipo(s) que no han quedado registrados: ` +
            faltan
              .slice(0, 4)
              .map((d) => `${d.name} (${d.mac})${d.reason ? ` — ${d.reason}` : ''}`)
              .join(', '),
          );
        } else if (fusionados.length) {
          setError(
            'Hay equipos distintos que han acabado en la misma ficha (¿serie o MAC repetida?): ' +
            fusionados
              .slice(0, 3)
              .map((item) => item.devices.map((d) => d.name).join(' + '))
              .join(' · '),
          );
        } else if (errores.length) {
          setError(
            errores
              .slice(0, 3)
              .map((item) => `${item.credential ? `${item.credential}: ` : ''}${item.error}`)
              .join(' · '),
          );
        } else if (avisos.length) {
          avisar(`${avisos[0].warning}`);
        }
      } else if (res?.mode === 'mixed') {
        avisar('Ámbitos de API barridos desde el servidor; el resto queda encolado en el colector.');
      } else {
        avisar('Barrido encolado. El colector lo ejecutará en el siguiente ciclo.');
      }
      setTimeout(cargar, 1500);
    } catch (e) {
      setError(e.message || 'No se pudo lanzar el barrido');
    } finally {
      setScanning(false);
    }
  };

  const guardarCredencial = async (payload) => {
    if (credentialDialog?.id) {
      await api.updateNetworkCredential(companyId, credentialDialog.id, payload);
      avisar('Credencial actualizada');
    } else {
      await api.createNetworkCredential(companyId, payload);
      avisar('Credencial creada');
    }
    await cargar();
  };

  const guardarAmbito = async (payload) => {
    if (scopeDialog?.id) {
      await api.updateNetworkScope(companyId, scopeDialog.id, payload);
      avisar('Ámbito actualizado');
    } else {
      await api.createNetworkScope(companyId, payload);
      avisar('Ámbito creado');
    }
    await cargar();
  };

  const probarCredencial = async (credential) => {
    setTestDialog({ credential, loading: true, result: null, error: '' });
    try {
      const result = await api.testNetworkCredential(companyId, credential.id);
      setTestDialog({ credential, loading: false, result, error: '' });
    } catch (e) {
      setTestDialog({
        credential,
        loading: false,
        result: null,
        error: e.message || 'No se pudo probar la credencial',
      });
    }
  };

  const borrarCredencial = async (credential) => {
    try {
      await api.deleteNetworkCredential(companyId, credential.id);
      avisar('Credencial borrada');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo borrar la credencial');
    }
  };

  const borrarAmbito = async (scope) => {
    try {
      await api.deleteNetworkScope(companyId, scope.id);
      avisar('Ámbito borrado');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo borrar el ámbito');
    }
  };

  if (!companyId) {
    return <div className="list-hint">Selecciona una empresa para configurar el recolector de red.</div>;
  }

  if (loading) {
    return (
      <div className="list-loading">
        <div className="spinner-border text-primary" aria-hidden="true" />
      </div>
    );
  }

  const ultimaEjecucion = runs[0];
  const hayColector = collectors.length > 0;
  const hayCredencial = credentials.some((credential) => credential.enabled);
  const hayAmbito = scopes.some((scope) => scope.enabled);
  // Los ámbitos de tipo controlador/API los lee el propio servidor: no hace
  // falta instalar ningún colector. Solo los rangos CIDR (SNMP/SSH) lo exigen.
  const ambitosApi = scopes.filter(
    (scope) => scope.enabled && String(scope.kind || '').toLowerCase() === 'controller',
  );
  const necesitaColector = scopes.some(
    (scope) => scope.enabled && String(scope.kind || '').toLowerCase() !== 'controller',
  );
  const escaneoDesdeServidor = ambitosApi.length > 0 && !necesitaColector;
  const listo = hayCredencial && hayAmbito && (!necesitaColector || hayColector);

  return (
    <div className="list-page list-page--embedded">
      <div className="list-head">
        <div className="list-head-left">
          <SignalIcon className="list-head-icon" />
          <div>
            <h2 className="list-section-title">Recolector de red</h2>
            <p className="list-section-subtitle">
              Perfiles de credenciales, rangos a descubrir y estado de los colectores.
              Los secretos se guardan cifrados y nunca se muestran.
            </p>
          </div>
        </div>
        <div className="list-head-actions">
          {aviso && (
            <span className="list-badge list-badge--on">
              <CheckIcon style={{ width: 13, height: 13, verticalAlign: '-2px' }} /> {aviso}
            </span>
          )}
          <button type="button" className="list-btn-secondary" onClick={() => setGuideOpen(true)}>
            <BookOpenIcon style={{ width: 14, height: 14 }} /> Guía SNMP / UniFi
          </button>
          <button type="button" className="list-btn-secondary" onClick={cargar}>
            <ArrowPathIcon style={{ width: 14, height: 14 }} /> Actualizar
          </button>
          <button
            type="button"
            className="list-btn-primary"
            onClick={lanzarBarrido}
            disabled={scanning || !listo}
            title={
              listo
                ? escaneoDesdeServidor
                  ? 'Leer el controlador desde el servidor (sin colector)'
                  : 'Encolar un barrido en el colector'
                : 'Completa la puesta en marcha antes de escanear'
            }
          >
            <ArrowPathIcon style={{ width: 14, height: 14 }} />
            {scanning ? ' Barriendo…' : ' Escanear ahora'}
          </button>
        </div>
      </div>

      {error && (
        <div className="list-badge list-badge--off" style={{ display: 'block', marginBottom: 12 }}>{error}</div>
      )}

      {notas.length > 0 && (
        <div
          className="list-badge list-badge--warn"
          style={{ display: 'block', marginBottom: 12, whiteSpace: 'pre-line', lineHeight: 1.5 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <strong>El barrido ha respetado las fichas que ya existían</strong>
            <button
              type="button"
              className="list-btn-secondary"
              style={{ padding: '0 8px' }}
              onClick={() => setNotas([])}
              title="Ocultar"
            >
              ✕
            </button>
          </div>
          {notas.map((texto) => `· ${texto}`).join('\n')}
        </div>
      )}

      {!listo && (
        <Panel
          title="Puesta en marcha"
          hint={
            escaneoDesdeServidor
              ? 'Tus ámbitos son de tipo controlador/API: el CMDB los lee directamente, sin colector.'
              : 'Sin estos pasos no se puede descubrir nada. «Escanear ahora» se activa al completarlos.'
          }
        >
          {escaneoDesdeServidor ? (
            <Paso
              numero={1}
              titulo="Colector (no hace falta con API)"
              detalle="Los ámbitos de controlador se consultan desde el propio servidor. Solo necesitas un colector si añades rangos SNMP/SSH."
              done
              action={(
                <button type="button" className="list-btn-secondary" onClick={() => setGuideOpen(true)}>
                  Cuándo hace falta
                </button>
              )}
            />
          ) : (
            <Paso
              numero={1}
              titulo="Instalar el colector"
              detalle="Una VM, un mini-PC o un contenedor en la VLAN de gestión, con rol network. Los rangos SNMP/SSH se barren desde él; los controladores por API no lo necesitan."
              done={hayColector}
              action={(
                <button type="button" className="list-btn-secondary" onClick={() => setGuideOpen(true)}>
                  Ver guía
                </button>
              )}
            />
          )}
          <Paso
            numero={2}
            titulo="Crear una credencial"
            detalle="Un perfil reutilizable: community SNMP, usuario SSH o API key de UniFi."
            done={hayCredencial}
            action={(
              <button type="button" className="list-btn-secondary" onClick={() => setCredentialDialog({ mode: 'create' })}>
                Nueva credencial
              </button>
            )}
          />
          <Paso
            numero={3}
            titulo="Crear un ámbito"
            detalle="Qué barrer: un rango CIDR o el controlador UniFi. Para UniFi, elige «Controlador (API)»."
            done={hayAmbito}
            action={(
              <button type="button" className="list-btn-secondary" onClick={() => setScopeDialog({ mode: 'create' })}>
                Nuevo ámbito
              </button>
            )}
          />
        </Panel>
      )}

      <div className="list-kpis list-kpis--chips" style={{ marginBottom: 14 }}>
        <span className="list-kpi-chip">
          <span className="list-kpi-value">{collectors.filter((c) => c.status === 'online').length}</span>
          <span className="list-kpi-label">colectores online</span>
        </span>
        <span className="list-kpi-chip">
          <span className="list-kpi-value">{credentials.filter((c) => c.enabled).length}</span>
          <span className="list-kpi-label">credenciales activas</span>
        </span>
        <span className="list-kpi-chip">
          <span className="list-kpi-value">{scopes.filter((s) => s.enabled).length}</span>
          <span className="list-kpi-label">ámbitos activos</span>
        </span>
        <span className="list-kpi-chip">
          <span className="list-kpi-value">{ultimaEjecucion ? ultimaEjecucion.device_count : 0}</span>
          <span className="list-kpi-label">equipos en el último barrido</span>
        </span>
      </div>

      {/* ── Colectores ── */}
      <Panel
        title="Colectores"
        hint="Agentes instalados con rol network. Solo hacen falta para barrer rangos SNMP/SSH: los controladores por API se leen desde el servidor."
        actions={<span className="list-badge">{collectors.length} registrados</span>}
      >
        {collectors.length === 0 ? (
          <p className="list-hint" style={{ margin: 0 }}>
            {escaneoDesdeServidor
              ? 'No hay colectores, y con tus ámbitos de API no hacen falta. Si algún día añades un rango CIDR, instálalo con install_linux.sh --role network o con el contenedor cmdb-network-collector.'
              : <>Todavía no hay ningún colector. Instálalo con <code>install_linux.sh --role network</code>{' '}o con el contenedor <code>cmdb-network-collector</code>.</>}
          </p>
        ) : collectors.map((collector) => (
          <Fila key={collector.agent_id}>
            <span style={{ fontWeight: 600, minWidth: 160 }}>{collector.hostname || 'sin nombre'}</span>
            <span className="list-code" title={collector.agent_id}>{collector.agent_id.slice(0, 8)}…</span>
            <span className={`list-badge ${collector.status === 'online' ? 'list-badge--on' : 'list-badge--muted'}`}>
              {collector.status === 'online' ? 'Online' : 'Offline'}
            </span>
            <span className="list-hint">v{collector.agent_version || '?'}</span>
            <span className="list-hint">{collector.platform || ''}</span>
            <span className="list-hint">
              último contacto: {collector.last_seen_at ? hace(collector.last_seen_at) : 'nunca'}
            </span>
          </Fila>
        ))}
      </Panel>

      {/* ── Credenciales ── */}
      <Panel
        title="Credenciales de descubrimiento"
        hint="Perfiles reutilizables. El colector los prueba en orden hasta que uno responde."
        actions={(
          <button
            type="button"
            className="list-btn-secondary"
            onClick={() => setCredentialDialog({ mode: 'create' })}
          >
            <PlusIcon style={{ width: 14, height: 14 }} /> Nueva credencial
          </button>
        )}
      >
        {credentials.length === 0 ? (
          <p className="list-hint" style={{ margin: 0 }}>
            Sin credenciales no se puede descubrir nada. Empieza con una comunidad SNMP de solo lectura.
          </p>
        ) : credentials.map((credential) => (
          <Fila
            key={credential.id}
            actions={(
              <>
                <button
                  type="button"
                  className="list-action-btn"
                  title="Probar (ver sites del controlador)"
                  onClick={() => probarCredencial(credential)}
                >
                  <PlayIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  title="Editar"
                  onClick={() => setCredentialDialog(credential)}
                >
                  <PencilSquareIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn list-action-btn--danger"
                  title="Borrar"
                  onClick={() => setConfirmar({
                    titulo: 'Borrar credencial',
                    mensaje: <>Se borrará <strong>{credential.name}</strong>. Los ámbitos que la usen dejarán de funcionar.</>,
                    onConfirm: () => borrarCredencial(credential),
                  })}
                >
                  <TrashIcon />
                </button>
              </>
            )}
          >
            <KeyIcon style={{ width: 15, height: 15, color: 'var(--text-secondary)' }} />
            <span style={{ fontWeight: 600, minWidth: 150 }}>{credential.name}</span>
            <span className="list-badge list-badge--muted">{KIND_LABEL[credential.kind] || credential.kind}</span>
            <span className="list-hint">prioridad {credential.priority}</span>
            <span className={`list-badge ${credential.enabled ? 'list-badge--on' : 'list-badge--muted'}`}>
              {credential.enabled ? 'Activa' : 'Inactiva'}
            </span>
            {credential.has_secret && <span className="list-hint">secreto guardado</span>}
          </Fila>
        ))}
      </Panel>

      {/* ── Ámbitos ── */}
      <Panel
        title="Ámbitos de barrido"
        hint="Un rango CIDR o un controlador, con sus credenciales y su agenda."
        actions={(
          <button
            type="button"
            className="list-btn-secondary"
            onClick={() => setScopeDialog({ mode: 'create' })}
          >
            <PlusIcon style={{ width: 14, height: 14 }} /> Nuevo ámbito
          </button>
        )}
      >
        {scopes.length === 0 ? (
          <p className="list-hint" style={{ margin: 0 }}>
            Añade un ámbito para que el colector sepa qué barrer. Ejemplo: la VLAN de gestión.
          </p>
        ) : scopes.map((scope) => (
          <Fila
            key={scope.id}
            actions={(
              <>
                <button
                  type="button"
                  className="list-action-btn"
                  title="Editar"
                  onClick={() => setScopeDialog(scope)}
                >
                  <PencilSquareIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn list-action-btn--danger"
                  title="Borrar"
                  onClick={() => setConfirmar({
                    titulo: 'Borrar ámbito',
                    mensaje: <>Se borrará <strong>{scope.name}</strong>. Los equipos ya descubiertos se conservan.</>,
                    onConfirm: () => borrarAmbito(scope),
                  })}
                >
                  <TrashIcon />
                </button>
              </>
            )}
          >
            <GlobeAltIcon style={{ width: 15, height: 15, color: 'var(--text-secondary)' }} />
            <span style={{ fontWeight: 600, minWidth: 150 }}>{scope.name}</span>
            <span className="list-badge list-badge--muted">
              {scope.kind === 'controller' ? 'Controlador' : 'Rango'}
            </span>
            <span className="list-code">{scope.cidr || 'API'}</span>
            <span className="list-hint">{scope.credential_ids?.length || 0} credencial(es)</span>
            <span className="list-hint">cada {scope.interval_minutes} min</span>
            <span className="list-hint">
              {scope.last_run_at ? `último ${hace(scope.last_run_at)}` : 'sin ejecutar'}
            </span>
            <span className={`list-badge ${scope.enabled ? 'list-badge--on' : 'list-badge--muted'}`}>
              {scope.enabled ? 'Activo' : 'Inactivo'}
            </span>
          </Fila>
        ))}
      </Panel>

      {/* ── Ejecuciones ── */}
      <Panel
        title="Últimas ejecuciones"
        hint="Resumen de cada barrido: equipos encontrados y errores. El detalle por IP (botón de la derecha) solo aparece con colector 2.2.8 o superior."
        actions={<span className="list-badge">{runs.length} registros</span>}
      >
        {runs.length === 0 ? (
          <p className="list-hint" style={{ margin: 0 }}>Todavía no se ha ejecutado ningún barrido.</p>
        ) : runs.map((run) => (
          <Fila
            key={run.id}
            actions={tieneInformeIp(run) ? (
              <button
                type="button"
                className="list-action-btn"
                title="Qué ha pasado con cada IP"
                onClick={() => setProbeDialog(run)}
              >
                <SignalIcon />
              </button>
            ) : null}
          >
            <span className={`list-badge ${RUN_BADGE[run.status] || 'list-badge--muted'}`}>
              {run.status}
            </span>
            <span className="list-hint">
              {run.started_at ? new Date(run.started_at).toLocaleString() : '—'}
            </span>
            <span className="list-hint">{run.device_count} equipo(s)</span>
            <span className="list-hint">{run.created_count} nuevos</span>
            <span className="list-hint">{run.updated_count} actualizados</span>
            {tieneInformeIp(run) && (
              <span
                className={`list-badge ${
                  run.summary.probe_summary.ok ? 'list-badge--on' : 'list-badge--off'
                }`}
                title="IPs que contestaron / IPs sondeadas"
              >
                {run.summary.probe_summary.ok || 0}/{run.summary.targets_total ?? '?'} IP
              </span>
            )}
            {run.error_count > 0 && (
              <span className="list-badge list-badge--warn">{run.error_count} error(es)</span>
            )}
            <span className="list-hint">{run.duration_seconds ? `${run.duration_seconds}s` : ''}</span>
            <span className="list-badge list-badge--muted">
              {run.collector_agent_id ? 'colector' : 'servidor (API)'}
            </span>
            <span className="list-hint">{run.reason}</span>
          </Fila>
        ))}
      </Panel>

      {credentialDialog && (
        <CredentialDialog
          initial={credentialDialog.mode === 'create' ? null : credentialDialog}
          onClose={() => setCredentialDialog(null)}
          onSave={guardarCredencial}
        />
      )}

      {scopeDialog && (
        <ScopeDialog
          initial={scopeDialog.mode === 'create' ? null : scopeDialog}
          credentials={credentials}
          onClose={() => setScopeDialog(null)}
          onSave={guardarAmbito}
        />
      )}

      {guideOpen && <AgentNetworkGuide onClose={() => setGuideOpen(false)} />}

      {probeDialog && (
        <ProbeDialog run={probeDialog} onClose={() => setProbeDialog(null)} />
      )}

      {testDialog && (
        <Dialog
          title={`Probar credencial · ${testDialog.credential.name}`}
          onClose={() => setTestDialog(null)}
          maxWidth={580}
          footer={(
            <button type="button" className="list-btn-primary" onClick={() => setTestDialog(null)}>
              Cerrar
            </button>
          )}
        >
          {testDialog.loading && (
            <div className="list-loading">
              <div className="spinner-border text-primary" aria-hidden="true" />
            </div>
          )}

          {!testDialog.loading && testDialog.error && (
            <div className="list-badge list-badge--off" style={{ display: 'block' }}>
              {testDialog.error}
            </div>
          )}

          {!testDialog.loading && testDialog.result && !testDialog.result.supported && (
            <p className="list-hint" style={{ margin: 0 }}>{testDialog.result.message}</p>
          )}

          {!testDialog.loading && testDialog.result && testDialog.result.supported && !testDialog.result.ok && (
            <div className="list-badge list-badge--off" style={{ display: 'block' }}>
              {testDialog.result.error}
            </div>
          )}

          {!testDialog.loading && testDialog.result && testDialog.result.supported && testDialog.result.ok && (
            <>
              {testDialog.result.mode === 'site_manager' && (
                <div
                  className="list-badge list-badge--warn"
                  style={{ display: 'block', marginBottom: 12, padding: '10px 12px' }}
                >
                  <strong>Site Manager (cloud)</strong>: inventario básico de equipos y
                  sites. No trae puertos, VLANs, LLDP, ARP ni cableado.
                </div>
              )}

              {testDialog.result.api_device_warning && (
                <div
                  className="list-badge list-badge--off"
                  style={{ display: 'block', marginBottom: 12, padding: '10px 12px' }}
                >
                  {testDialog.result.api_device_warning}
                </div>
              )}

              {testDialog.result.all_sites && (testDialog.result.sites || []).length > 1 && (
                <div
                  className="list-badge list-badge--warn"
                  style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, padding: '10px 12px' }}
                >
                  <ExclamationTriangleIcon style={{ width: 16, height: 16, flexShrink: 0, marginTop: 2 }} />
                  <span>
                    {testDialog.result.mode === 'site_manager' ? (
                      <>
                        Esta API key ve <strong>{testDialog.result.sites.length} sites</strong>. El
                        barrido de Site Manager exige indicar en <strong>Site / host</strong> el site
                        o el host de este cliente; si no, se detiene para no mezclar empresas.
                      </>
                    ) : (
                      <>
                        Esta API key ve <strong>{testDialog.result.sites.length} sites</strong>. Sin site
                        configurado el colector los leerá <strong>todos</strong> y los guardará en esta empresa.
                        Si son clientes distintos, usa un colector por cliente o indica el site en la credencial.
                      </>
                    )}
                  </span>
                </div>
              )}

              {testDialog.result.configured_site && !testDialog.result.site_found && (
                <div
                  className="list-badge list-badge--warn"
                  style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, padding: '10px 12px' }}
                >
                  <ExclamationTriangleIcon style={{ width: 16, height: 16, flexShrink: 0, marginTop: 2 }} />
                  <span>
                    El site <strong>{testDialog.result.configured_site}</strong> no aparece en el
                    controlador: el barrido no devolverá equipos. Corrige el nombre en la credencial.
                  </span>
                </div>
              )}

              <div className="list-hint" style={{ marginBottom: 6 }}>
                Sites visibles para esta API key
              </div>
              {(testDialog.result.sites || []).length === 0 && (
                <p className="list-hint" style={{ margin: 0 }}>
                  El controlador no devolvió ningún site.
                </p>
              )}
              {(testDialog.result.sites || []).map((site) => (
                <Fila key={site.id}>
                  <span style={{ fontWeight: 600, minWidth: 160 }}>
                    {site.name || site.id}
                  </span>
                  {site.internal_reference && (
                    <span className="list-code">{site.internal_reference}</span>
                  )}
                  <span className="list-hint">
                    {site.device_count === null ? '—' : site.device_count} dispositivo(s)
                  </span>
                  <span className="list-hint">
                    {site.client_count === null ? '—' : site.client_count} cliente(s)
                  </span>
                  {site.device_count > 0 && site.api_device_count === 0 && (
                    <span
                      className="list-badge list-badge--warn"
                      title="Site Manager no publica el detalle de los equipos de este host (Network Server autoalojado)"
                    >
                      API sin detalle
                    </span>
                  )}
                  {site.selected && (
                    <span className="list-badge list-badge--on">site de la credencial</span>
                  )}
                </Fila>
              ))}

              {testDialog.result.all_sites && (testDialog.result.sites || []).length === 1 && (
                <p className="list-hint" style={{ marginTop: 10, marginBottom: 0 }}>
                  Solo hay un site: no hace falta indicarlo en la credencial.
                </p>
              )}
            </>
          )}
        </Dialog>
      )}

      {confirmar && (
        <Dialog
          title={confirmar.titulo}
          onClose={() => setConfirmar(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setConfirmar(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={async () => {
                  const accion = confirmar.onConfirm;
                  setConfirmar(null);
                  await accion();
                }}
              >
                Borrar
              </button>
            </>
          )}
        >
          <p style={{ margin: 0 }}>{confirmar.mensaje}</p>
        </Dialog>
      )}
    </div>
  );
}
